import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import type { EventPreview } from '../lib/eventPreview';
import { sanitizedPageFields, trackEvent } from '../lib/analytics';
import { fetchAppPublishedStatus } from '../config/appStore';
import AppStoreBadges from './AppStoreBadges';
import { detectPlatform, buildAndroidIntentUrl, buildIosEventUrl } from '../lib/platform';
import { openInviteTarget } from '../lib/inviteNavigation';

interface Props {
  event: EventPreview;
  /** Invite credential — only ever embedded in launch targets, never rendered. */
  inviteCode?: string;
}

const LAUNCH_FALLBACK_MS = 2500;

/**
 * Platform-aware call-to-action for the shared event composition.
 *
 * - iOS: "Open in RidePulse" dispatches the ridepulse://event/{id} deep link
 *   (invite code preserved in the query) via window.location.assign.
 * - Android: same button dispatches an intent:// launcher URL targeting
 *   package app.ridepulse.android with the canonical HTTPS event URL as the
 *   browser fallback (invite code preserved).
 * - Desktop/other: the official Apple and Google Play badges together — no
 *   fake app launch.
 * Dispatch is explicit-click only; a visible fallback note appears if the app
 * may not have opened — it never claims install or join success. The invite
 * code never appears in text, analytics, or logs — handlers emit
 * hasInviteCode booleans only.
 */
const EventInviteCta: React.FC<Props> = ({ event, inviteCode }) => {
  const { colors, dsColors } = useTheme();
  const platform = detectPlatform();
  const [mayNotHaveOpened, setMayNotHaveOpened] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'ok' | 'failed'>('idle');
  const fallbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dispatchPending = useRef(false);
  const copyPending = useRef(false);
  const mounted = useRef(true);
  const copyGenRef = useRef(0);

  const analyticsBase = {
    eventId: event.id,
    hasInviteCode: !!inviteCode,
    isPublic: event.isPublic,
    ...sanitizedPageFields(),
  };

  const clearFallbackTimer = () => {
    if (fallbackTimer.current) {
      clearTimeout(fallbackTimer.current);
      fallbackTimer.current = null;
    }
    dispatchPending.current = false;
  };

  // New event or code invalidates any pending fallback message and any
  // in-flight copy attempt — a stale clipboard resolution may never write
  // status for the newly rendered invitation or release a newer guard.
  const launchKey = `${event.id}:${inviteCode ?? ''}`;
  useEffect(() => {
    // Monotonic generation: a deferred clipboard completion may never write
    // status or release the pending guard for a different invitation —
    // including A→B→A where the key string repeats.
    copyGenRef.current += 1;
    setMayNotHaveOpened(false);
    setCopyState('idle');
    copyPending.current = false;
    clearFallbackTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [launchKey]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    // The page hiding cancels the pending notice — that is never proof the
    // app opened, just that the timer should not fire underneath it.
    const cancel = () => clearFallbackTimer();
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') cancel();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', cancel);
    return () => {
      cancel();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', cancel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAppPublishedStatus().catch(() => {});
  }, []);

  const handleBadgeClick = (badgePlatform: 'apple' | 'android') => {
    trackEvent('invite_download_click', {
      ...analyticsBase,
      store: badgePlatform === 'apple' ? 'ios' : 'android',
    });
  };

  const isTerminal = event.status === 'cancelled' || event.status === 'completed';
  const isInviteOnly = !event.isPublic;
  const helperText = isTerminal
    ? 'Open RidePulse to view this event’s details.'
    : isInviteOnly
      ? 'Open RidePulse to review the invitation and confirm joining.'
      : 'Choose your motorcycle and join in RidePulse.';

  const primaryClass =
    'inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold shadow-lg hover:shadow-xl transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2';
  const primaryStyle: React.CSSProperties = {
    backgroundColor: dsColors.primaryStrong,
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
  };
  const quietClass =
    'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2';
  const quietStyle: React.CSSProperties = {
    backgroundColor: 'transparent',
    color: colors.textPrimary,
    border: `1px solid ${colors.border}`,
    cursor: 'pointer',
  };

  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'https://ridepulse.app';

  const handleOpenInAppClick = () => {
    if (dispatchPending.current) return; // one dispatch per pending attempt
    trackEvent('invite_open_app_click', { ...analyticsBase, platform });
    const target =
      platform === 'ios'
        ? buildIosEventUrl(event.id, inviteCode)
        : buildAndroidIntentUrl(event.id, inviteCode, origin);
    // A retry clears the prior failure note until the new timer decides.
    setMayNotHaveOpened(false);
    try {
      dispatchPending.current = true;
      openInviteTarget(target);
    } catch {
      dispatchPending.current = false;
      setMayNotHaveOpened(true);
      return;
    }
    fallbackTimer.current = setTimeout(() => {
      fallbackTimer.current = null;
      dispatchPending.current = false;
      setMayNotHaveOpened(true);
    }, LAUNCH_FALLBACK_MS);
  };

  // Explicit click only — the code is never rendered, logged, stored, or sent
  // to analytics; the outcome note is neutral either way.
  const handleCopyCodeClick = async () => {
    if (!inviteCode || copyPending.current) return;
    copyPending.current = true;
    const generation = copyGenRef.current;
    setCopyState('idle');
    try {
      await navigator.clipboard.writeText(inviteCode);
      if (mounted.current && generation === copyGenRef.current) setCopyState('ok');
    } catch {
      if (mounted.current && generation === copyGenRef.current) setCopyState('failed');
    } finally {
      if (generation === copyGenRef.current) copyPending.current = false;
    }
  };

  const copyAndHelp = inviteCode && (
    <div className="flex flex-col items-start gap-2">
      <button type="button" onClick={handleCopyCodeClick} className={quietClass} style={quietStyle}>
        Copy invite code
      </button>
      <p className="text-[11px] m-0" role="status" aria-live="polite" style={{ color: colors.textTertiary }}>
        {copyState === 'ok' && 'Invite code copied.'}
        {copyState === 'failed' && 'Could not copy — ask the host to resend the code.'}
      </p>
      <p className="text-[11px] m-0" style={{ color: colors.textTertiary }}>
        You can use the code with “Enter invite code” in supported app builds.
      </p>
    </div>
  );

  const helpLink = (
    <p className="text-[11px] m-0" style={{ color: colors.textTertiary }}>
      <Link to="/help#join-event" className="underline" style={{ color: 'inherit' }}>
        How joining works
      </Link>
    </p>
  );

  // Always-visible honest guidance: the app can't be detected from a browser,
  // so a browser-side remount (e.g. Chrome's intent fallback) still leaves
  // the visitor a working path. It never claims install or open success.
  const alwaysFallbackNote = (
    <p className="text-[11px] m-0" style={{ color: colors.textSecondary }}>
      If RidePulse does not open, open the installed app directly or install
      or update it, then reopen this invitation link.
    </p>
  );

  const fallbackNote = mayNotHaveOpened && (
    <p className="text-[11px] m-0" role="status" style={{ color: colors.textSecondary }}>
      RidePulse may not have opened — try again, or use the options below.
    </p>
  );

  if (platform === 'ios' || platform === 'android') {
    return (
      <div className="flex flex-col items-start gap-3">
        <button type="button" onClick={handleOpenInAppClick} className={primaryClass} style={primaryStyle}>
          <span>Open in RidePulse</span>
        </button>
        <AppStoreBadges
          layout="horizontal"
          size="small"
          showApple={platform === 'ios'}
          showAndroid={platform === 'android'}
          onBadgeClick={handleBadgeClick}
          align="start"
        />
        {copyAndHelp}
        <p className="text-[11px] m-0" style={{ color: colors.textTertiary }}>
          {helperText}
        </p>
        {alwaysFallbackNote}
        {fallbackNote}
        {helpLink}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start gap-3">
      <AppStoreBadges
        layout="horizontal"
        size="small"
        showApple
        showAndroid
        onBadgeClick={handleBadgeClick}
        align="start"
      />
      {copyAndHelp}
      <p className="text-[11px] m-0" style={{ color: colors.textTertiary }}>
        {helperText}
      </p>
      {helpLink}
    </div>
  );
};

export default EventInviteCta;
